import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Send, Mail, Eye, CheckCircle, XCircle, Trash2, Copy, Clock, UserCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ClientIntakePanel = ({ caseId, agent, onUpdate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [reviewing, setReviewing] = useState(null); // intake id being reviewed

  const fetchItems = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/compliance/cases/${caseId}/intake-requests`);
      if (r.ok) setItems(await r.json());
    } catch (e) { console.error(e); }
  }, [caseId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const sendIntake = async () => {
    if (!window.confirm('Send a self-serve intake link to the client by email?')) return;
    setLoading(true); setLastResult(null);
    try {
      const r = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/intake-requests?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send intake link');
      }
      const data = await r.json();
      try { await navigator.clipboard.writeText(data.intake_url); } catch (e) { console.warn('Clipboard unavailable — intake URL not auto-copied:', e); }
      setLastResult({
        kind: 'success',
        message: `Intake link sent to ${data.intake_request.client_email}. Copied to clipboard.`,
        url: data.intake_url,
        email_status: data.intake_request.email_status,
      });
      fetchItems();
    } catch (e) {
      setLastResult({ kind: 'error', message: e.message });
    } finally { setLoading(false); }
  };

  const accept = async (id) => {
    if (!window.confirm('Merge this client submission into the case? This will update the client record, fact-find, and needs analysis.')) return;
    const r = await fetch(
      `${API_URL}/api/compliance/cases/${caseId}/intake-requests/${id}/accept?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
      { method: 'POST' },
    );
    if (r.ok) {
      setLastResult({ kind: 'success', message: 'Submission accepted and merged.' });
      fetchItems();
      onUpdate && onUpdate();
    } else {
      const err = await r.json().catch(() => ({}));
      setLastResult({ kind: 'error', message: err.detail || 'Accept failed' });
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Reason for rejecting this submission (will not be visible to the client):');
    if (reason === null) return;
    const r = await fetch(
      `${API_URL}/api/compliance/cases/${caseId}/intake-requests/${id}/reject?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) },
    );
    if (r.ok) { fetchItems(); }
  };

  const remove = async (id) => {
    if (!window.confirm('Permanently delete this intake request?')) return;
    await fetch(
      `${API_URL}/api/compliance/cases/${caseId}/intake-requests/${id}?advisor_id=${agent.id}`,
      { method: 'DELETE' },
    );
    fetchItems();
  };

  const copyUrl = async (token) => {
    const url = `${window.location.origin}/intake/${token}`;
    try { await navigator.clipboard.writeText(url); setLastResult({ kind: 'success', message: 'Copied: ' + url }); }
    catch { prompt('Copy link:', url); }
  };

  const statusBadge = (status) => {
    const map = {
      pending: 'bg-yellow-100 text-yellow-700',
      started: 'bg-blue-100 text-blue-700',
      submitted: 'bg-purple-100 text-purple-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      expired: 'bg-gray-200 text-gray-600',
    };
    return `px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || map.pending}`;
  };

  const reviewItem = items.find(it => it.id === reviewing);

  return (
    <Card data-testid="client-intake-panel">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="flex items-center gap-2"><UserCheck className="h-5 w-5 text-[#003D5C]" />Self-Serve Client Intake</CardTitle>
        <Button onClick={sendIntake} disabled={loading} size="sm" className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]" data-testid="send-intake-btn">
          <Send className="h-4 w-4 mr-1" />{loading ? 'Sending…' : 'Send Intake to Client'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">
          Email the client a secure link so they can fill the <b>About You</b>, <b>Fact-Find</b>, and <b>Needs Analysis</b> sections themselves.
          Their answers will appear here for your review — accept to merge them into the case.
        </p>

        {lastResult && (
          <div className={`border rounded-lg p-3 text-sm ${lastResult.kind === 'success' ? 'bg-green-50 border-green-300 text-green-900' : 'bg-red-50 border-red-300 text-red-900'}`} data-testid="intake-result-panel">
            <div className="flex justify-between items-start gap-2">
              <span>{lastResult.kind === 'success' ? '✓ ' : '⚠ '}{lastResult.message}</span>
              <button onClick={() => setLastResult(null)} className="text-xs opacity-60">Dismiss ✕</button>
            </div>
            {lastResult.url && <p className="mt-1 text-xs font-mono break-all">{lastResult.url}</p>}
          </div>
        )}

        {items.length === 0 ? (
          <p className="text-gray-500 text-center py-4 text-sm">No intake requests sent yet.</p>
        ) : (
          <div className="space-y-2">
            {items.map(it => (
              <div key={it.id} className="border rounded-lg p-3" data-testid={`intake-req-${it.id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-medium text-sm">{it.client_name}</span>
                  <span className={statusBadge(it.status)} data-testid={`intake-status-${it.id}`}>{it.status}</span>
                  {it.email_status === 'sent' && <span className="text-xs text-green-700">✅ Email sent</span>}
                  {it.email_status === 'failed' && <span className="text-xs text-red-700">⚠️ Email failed</span>}
                  {it.submitted_at && (
                    <span className="text-xs text-gray-500"><Clock className="h-3 w-3 inline" /> submitted {new Date(it.submitted_at).toLocaleString()}</span>
                  )}
                </div>
                <p className="text-xs text-gray-500">{it.client_email}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {it.status === 'submitted' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setReviewing(it.id === reviewing ? null : it.id)} data-testid={`view-intake-${it.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-1" />{reviewing === it.id ? 'Hide' : 'Review'}
                      </Button>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => accept(it.id)} data-testid={`accept-intake-${it.id}`}>
                        <CheckCircle className="h-3.5 w-3.5 mr-1" />Accept & Merge
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(it.id)} data-testid={`reject-intake-${it.id}`}>
                        <XCircle className="h-3.5 w-3.5 mr-1 text-red-500" />Reject
                      </Button>
                    </>
                  )}
                  {it.status === 'pending' || it.status === 'started' ? (
                    <Button size="sm" variant="ghost" onClick={() => copyUrl(it.token)} data-testid={`copy-intake-${it.id}`}>
                      <Copy className="h-3.5 w-3.5 mr-1" />Copy Link
                    </Button>
                  ) : null}
                  <Button size="sm" variant="ghost" onClick={() => remove(it.id)} data-testid={`delete-intake-${it.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>

                {reviewing === it.id && reviewItem && (
                  <div className="mt-3 border-t pt-3 text-xs space-y-3" data-testid={`intake-review-${it.id}`}>
                    <SectionReview title="About You" data={reviewItem.client_data} />
                    <SectionReview title="Your Finances" data={reviewItem.fact_find_data} />
                    <SectionReview title="Your Goals" data={reviewItem.needs_analysis_data} />
                    {reviewItem.submitted_geo_lat && (
                      <p className="text-gray-500">📍 Submitted from {reviewItem.submitted_geo_lat.toFixed(4)}, {reviewItem.submitted_geo_lng.toFixed(4)} (IP {reviewItem.submitted_ip || 'unknown'})</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SectionReview = ({ title, data }) => {
  if (!data || Object.keys(data).length === 0) return null;
  const entries = Object.entries(data).filter(([k, v]) => v !== '' && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0));
  if (entries.length === 0) return null;
  return (
    <div className="bg-gray-50 border rounded p-2">
      <h5 className="font-semibold mb-1">{title}</h5>
      <div className="grid sm:grid-cols-2 gap-x-3 gap-y-0.5">
        {entries.map(([k, v]) => (
          <div key={k}>
            <span className="text-gray-500">{k.replace(/_/g, ' ')}:</span> <span className="font-medium">{Array.isArray(v) ? v.join(', ') : String(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
